import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";
import type {
  DashboardSnapshot,
  DashboardPeriod,
  DashboardLabels,
  DashboardAiInsight,
  DashboardUpcomingBooking,
  DashboardQuickAction,
} from "./types";
import { DashboardHero } from "./DashboardHero";
import { DashboardKpiGrid } from "./DashboardKpiGrid";
import { DashboardAiInsightStrip } from "./DashboardAiInsight";
import { DashboardRevenueChart } from "./DashboardRevenueChart";
import { DashboardBookingList } from "./DashboardBookingList";
import { DashboardReviewList } from "./DashboardReviewList";
import { DashboardStaffLeaderboard } from "./DashboardStaffLeaderboard";
import { DashboardOccupancyPanel } from "./DashboardOccupancyPanel";
import { DashboardQuickActionGrid } from "./DashboardQuickActionGrid";
import { DashboardSkeleton } from "./DashboardSkeleton";
import { DashboardErrorState } from "./DashboardErrorState";

export interface BusinessDashboardProps {
  snapshot: DashboardSnapshot;
  period: DashboardPeriod;
  onPeriodChange: (period: DashboardPeriod) => void;
  labels: DashboardLabels;
  now?: Date;

  onInsightCta?: (insight: DashboardAiInsight) => void;
  onInsightDismiss?: (insight: DashboardAiInsight) => void;
  onBookingClick?: (booking: DashboardUpcomingBooking) => void;
  onViewAllBookings?: () => void;
  onViewAllReviews?: () => void;
  onQuickAction?: (action: DashboardQuickAction) => void;

  isLoading?: boolean;
  error?: { message?: string } | null;
  onRetry?: () => void;

  className?: string;
  dataTestId?: string;
}

export function BusinessDashboard({
  snapshot,
  period,
  onPeriodChange,
  labels,
  now,
  onInsightCta,
  onInsightDismiss,
  onBookingClick,
  onViewAllBookings,
  onViewAllReviews,
  onQuickAction,
  isLoading,
  error,
  onRetry,
  className,
  dataTestId,
}: BusinessDashboardProps) {
  const reduceMotion = useReducedMotion();

  if (isLoading) {
    return <DashboardSkeleton className={className} loadingLabel={labels.states.loadingLabel} />;
  }

  if (error) {
    return (
      <div className={cn("p-4 sm:p-6", className)}>
        <DashboardErrorState
          title={labels.states.errorTitle}
          description={error.message ?? labels.states.errorDescription}
          retryLabel={labels.states.retryLabel}
          onRetry={onRetry}
        />
      </div>
    );
  }

  return (
    <motion.div
      data-testid={dataTestId}
      initial={reduceMotion ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.18 }}
      className={cn(
        "biz-scrollbar flex h-full w-full flex-col gap-4 overflow-y-auto bg-biz-bg p-4 sm:gap-5 sm:p-6",
        className,
      )}
    >
      <DashboardHero
        organizationName={snapshot.organizationName}
        branchName={snapshot.branchName}
        updatedAtIso={snapshot.updatedAt}
        now={now}
        period={period}
        onPeriodChange={onPeriodChange}
        labels={labels}
      />

      <DashboardKpiGrid stats={snapshot.stats} labels={labels} />

      <DashboardAiInsightStrip
        insights={snapshot.aiInsights}
        sectionTitle={labels.aiInsights.sectionTitle}
        sectionSubtitle={labels.aiInsights.sectionSubtitle}
        emptyLabel={labels.aiInsights.emptyLabel}
        dismissLabel={labels.aiInsights.dismissLabel}
        onCta={(i) => onInsightCta?.(i)}
        onDismiss={(i) => onInsightDismiss?.(i)}
      />

      <DashboardRevenueChart
        series={snapshot.revenueSeries}
        title={labels.revenueChart.title}
        subtitle={labels.revenueChart.subtitle}
        period={period}
        onPeriodChange={onPeriodChange}
        periodLabels={labels.periods}
        emptyLabel={labels.upcomingBookings.emptyLabel}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <DashboardBookingList
          bookings={snapshot.upcomingBookings}
          title={labels.upcomingBookings.title}
          viewAllLabel={labels.upcomingBookings.viewAllLabel}
          emptyLabel={labels.upcomingBookings.emptyLabel}
          onBookingClick={onBookingClick}
          onViewAll={onViewAllBookings}
        />
        <DashboardReviewList
          reviews={snapshot.recentReviews}
          title={labels.reviews.title}
          viewAllLabel={labels.reviews.viewAllLabel}
          emptyLabel={labels.reviews.emptyLabel}
          onViewAll={onViewAllReviews}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <DashboardStaffLeaderboard
          entries={snapshot.staffLeaderboard}
          title={labels.staffLeaderboard.title}
          completedLabel={labels.staffLeaderboard.completedLabel}
          revenueLabel={labels.staffLeaderboard.revenueLabel}
          ratingLabel={labels.staffLeaderboard.ratingLabel}
          emptyLabel={labels.staffLeaderboard.emptyLabel}
        />
        <DashboardOccupancyPanel
          entries={snapshot.occupancy}
          title={labels.occupancy.title}
          bookingsLabel={labels.occupancy.bookingsLabel}
          emptyLabel={labels.occupancy.emptyLabel}
        />
      </div>

      <DashboardQuickActionGrid
        actions={snapshot.quickActions}
        labels={labels.quickActions}
        onAction={onQuickAction}
      />
    </motion.div>
  );
}
